import { useContext, useEffect, useState, useTransition } from "react";
import { FaPlus, FaTrashAlt } from "react-icons/fa";
import { FaSort } from "react-icons/fa6";
import { IoSearchOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import Context from "../../../common/components/utils/context";
import {
  GroupsV1Service,
  AccountsV1Service,
} from "../../../common/services/api/api";
import { IContact, IGroupMembers } from "../../../common/models/accounts";
import { Themes } from "../../../common/enums/themes";
import { IModal, Modal } from "../../../common/components/modal/modal";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { Loading } from "../../../common/components/loading/loading";
import { MdEdit } from "react-icons/md";
import { Circles } from "react-loader-spinner";

export interface option {
  readonly value: string;
  readonly label: string;
  readonly id?: string;
}

const ContactsGroups = () => {
  const context = useContext(Context);
  const [, startTransition] = useTransition();
  const navigate = useNavigate();
  const animatedComponents = makeAnimated();
  const groupService = new GroupsV1Service();
  const accountService = new AccountsV1Service();
  const [contacts, setContacts] = useState<IContact[]>([]);
  const [groups, setGroups] = useState<IGroupMembers[]>([]);
  const [filterResult, setFilterResult] = useState<IGroupMembers[]>([]);
  const [userId, setUserId] = useState<string>();
  const [modal, setModal] = useState<IModal>();
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState<IGroupMembers>();
  const [changeType, setChangeType] = useState<string>();
  const [item, setItem] = useState<IGroupMembers>();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGroupsLoading, setIsGroupsLoading] = useState<boolean>(true);

  const options: readonly option[] = contacts?.map((ct) => ({
    value: ct.accountId,
    id: ct.userId,
    label: `${ct.username} - ${ct.email}`,
  }));

  const toggleShowModal = (type: string) => {
    setChangeType(type);
    setShowModal(!showModal);
  };

  const closeModal = () => {
    setSelected(undefined);
    setShowModal(false);
  };

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const { data: contacts } = await accountService.getEligibleContacts();

        const { data: account } = await accountService.get();
        const sortedGroups = [...account.groups].sort((a, b) =>
          a?.name?.localeCompare(b.name)
        );
        const seen = new Set();
        const filteredRelations = contacts.filter(({ accountId }) => {
          if (seen.has(accountId)) return false;
          seen.add(accountId);
          return true;
        });
        const sortedContacts = [...filteredRelations].sort((a, b) =>
          a?.username?.localeCompare(b.username)
        );
        startTransition(() => {
          setGroups(sortedGroups);
          setFilterResult(sortedGroups);
          setContacts(sortedContacts);
        });
        setUserId(account.accountId);
        // context.setAccount(account);
      } catch (error) {
        console.error("Error fetching Groups:", error);
      } finally {
        setIsGroupsLoading(false);
      }
    };

    fetchGroups();
  }, [isGroupsLoading]);

  useEffect(() => {
    setModal({
      show: showModal,
      size: 'lg',
      component: (
        <SearchSelect
          name="contacts"
          title={changeType}
          groupName={selected?.name}
          groupMembers={selected?.accounts}
          options={contacts ?? []}
          onClose={closeModal}
        />
      ),
    });
  }, [showModal]);

  const SearchSelect = (props: any) => {
    const [memberIds, setMemberIds] = useState<string[]>(
      props.groupMembers
        ?.filter((gm: any) => gm.accountId !== userId)
        ?.map((m: any) => m.userId) || []
    );
    const [groupName, setGroupName] = useState<string>(props.groupName || "");
    const [selectedOptions, setSelectedOptions] = useState(
      options.filter((option) =>
        props.groupMembers?.some((m: any) => m.accountId === option.value)
      )
    );
    const onSelectContactChange = (c: any) => {
      const newValue = c.map((a: any) => a.id);
      const newSelectedOptionValue = options.filter((option) =>
        newValue.some((m: any) => m === option.id)
      );
      setMemberIds((output) => [...newValue]);
      setSelectedOptions(newSelectedOptionValue);
    };

    const createOrUpdateGroup = async () => {
      const previousMembers = props.groupMembers
        ?.filter((m: any) => m.accountId !== userId)
        ?.map((mId: any) => mId.userId);
      const newGroupName = groupName.trim();
      if (memberIds.length === 0 && props.title === "create") {
        context.setAlert({
          message: `Failed to create group!
               Please select at least one member`,
          theme: Themes.Danger,
        });
        setIsSubmitting(false);
      } else if (newGroupName.length === 0) {
        context.setAlert({
          message: `Failed to ${props.title} group!
               Please enter a valid group name`,
          theme: Themes.Danger,
        });
        setIsSubmitting(false);
      } else if (
        props.title === "update" &&
        memberIds.sort().join(",") === previousMembers.sort().join(",") &&
        newGroupName === props.groupName
      ) {
        setIsSubmitting(false);
        setModal({ show: false });
      } else {
        if (!newGroupName) {
          context.setAlert({
            message: `Failed to ${props.title} group!
               Please enter a valid group name`,
            theme: Themes.Danger,
          });
          setIsSubmitting(false);
          return;
        }
        try {
          if (props.title === "update") {
            if (memberIds.length === 0) {
              context.setAlert({
                message: `Failed to ${props.title} group!
                   Please select at least one member`,
                theme: Themes.Danger,
              });
              setIsSubmitting(false);
              return;
            }
            if (selected && newGroupName !== props.groupName) {
              const updateResult = await groupService.updateGroupName(
                selected.id,
                {
                  name: newGroupName,
                }
              );
              if (updateResult.status !== 200) {
                context.setAlert({
                  message: `Failed to ${props.title} group name!
               Please try again`,
                  theme: Themes.Danger,
                });
                setIsSubmitting(false);
                return;
              }
            }
            if (
              selected &&
              memberIds.sort().join(",") !== previousMembers.sort().join(",")
            ) {
              const membersUpdated = await syncMembers(
                selected.id,
                previousMembers,
                memberIds
              );
              if (!membersUpdated) {
                context.setAlert({
                  message: `Failed to ${props.title} group members!
               Please try again`,
                  theme: Themes.Danger,
                });
                setIsSubmitting(false);
                return;
              }
            }
            setIsSubmitting(false);
            setIsGroupsLoading(true);
            setModal({ show: false });
            context.setAlert({
              message: "Group updated successfully!",
              theme: Themes.Success,
            });
          } else {
            const accountIds = props.options
              .filter((user: any) => memberIds.includes(user.userId))
              .map((user: any) => user.accountId);
            const params = {
              name: newGroupName,
              userIds: accountIds,
            };
            const response = await groupService.createGroup(params);
            if (response.status === 201) {
              const groupMembersRequestData =
                await groupService.getGroupMembers(response.data.id);
              const groupMembersData = groupMembersRequestData.data;
              var newData = [groupMembersData, ...groups].sort((a, b) =>
                a?.name?.localeCompare(b.name)
              );
              setGroups(newData);
              setFilterResult(newData);
              setIsSubmitting(false);
              setModal({ show: false });
              context.setAlert({
                message: "Group created successfully!",
                theme: Themes.Success,
              });
            } else {
              context.setAlert({
                message: `Failed to ${props.title} group!
               Please try again`,
                theme: Themes.Danger,
              });
              setIsSubmitting(false);
              return;
            }
          }
        } catch (error) {
          console.error("Error creating or updating group", error);
          context.setAlert({
            message:
              "An error has occurred. Please try again or contact your administrator.",
            theme: Themes.Danger,
          });
          setIsSubmitting(false);
        }
      }
    };

    const syncMembers = async (
      groupId: string,
      initialMembers: string[],
      finalMembers: string[]
    ) => {
      let succeeded: boolean = false;
      const initialSet = new Set(initialMembers);
      const finalSet = new Set(finalMembers);
      try {
        for (const removeId of initialSet) {
          if (!finalSet.has(removeId)) {
            await groupService.removeGroupMember(groupId, removeId);
          }
        }
        for (const addId of finalSet) {
          if (!initialSet.has(addId)) {
            await groupService.addGroupMember(groupId, addId);
          }
        }
        succeeded = true;
      } catch (error) {
        console.error("Error syncing members", error);
      }
      return succeeded;
    };

    return (
      <div className="flex flex-col h-lvh w-full max-w-2xl mx-auto bg-white rounded-xl overflow-hidden shadow-lg p-6 overflow-y-scroll">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            {props.title === "create" ? "New" : "Update"} Group
          </h2>
        </div>
        <div className="mb-6">
          <fieldset className="w-full">
            <label htmlFor="groupName" className="text-gray-500 text-sm">
              Name*
            </label>
            <input
              type="text"
              placeholder="Enter group name"
              id="groupName"
              name="groupName"
              value={groupName}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-400 text-sm"
              onChange={(e) => setGroupName(e.target.value)}
            />
          </fieldset>
        </div>
        <div className="mb-6">
          <label
            htmlFor="groupMembers"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Members
          </label>
          <Select
            components={animatedComponents}
            isMulti
            id="groupMembers"
            name="groupMembers"
            value={selectedOptions}
            options={options}
            className="basic-multi-select focus:outline-none focus:ring-2 focus:ring-cyan-200"
            classNamePrefix="select"
            onChange={onSelectContactChange}
          />
        </div>
        <div className="mb-6 mt-auto">
          <button
            type="button"
            className="flex items-center gap-x-2 rounded-md capitalize text-white bg-sky-400 hover:bg-sky-600 px-10 py-2"
            onClick={() => {
              setIsSubmitting(true);
              createOrUpdateGroup();
            }}
            disabled={isSubmitting}
          >
            <span>{props.title}</span>
          </button>
        </div>
      </div>
    );
  };

  const deleteGroup = async () => {
    if (!context.account) return;
    try {
      if (!item?.id) {
        context.setAlert({
          message: "Failed to delete group! Group ID is unavailable.",
          theme: Themes.Danger,
        });
        throw new Error("Group ID is undefined");
      }
      const response = await groupService.deleteGroup(item.id);
      if (response.status === 200) {
        const index = groups?.findIndex((idx) => idx.id === item?.id);
        if (index !== -1) {
          const newArray = [
            ...groups?.slice(0, index),
            ...groups?.slice(index + 1),
          ];
          setGroups(newArray);
          setFilterResult(newArray);
        }
        context.setAlert({
          message: "Group deleted successfully!",
          theme: Themes.Success,
        });
      } else {
        context.setAlert({
          message: "Failed to delete group!",
          theme: Themes.Danger,
        });
      }
    } catch (error) {
      console.error("Error deleting contact", error);
      context.setAlert({
        message: "An error occured while deleting the group. Please try again.",
        theme: Themes.Danger,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const filterGroups = (query: string) => {
    var result = groups.filter(
      (g) =>
        g.name.toLowerCase().includes(query.toLowerCase()) ||
        g.name.toLowerCase().includes(query.toLowerCase())
    );
    setFilterResult(result);
  };

  return isGroupsLoading ? (
    <Loading />
  ) : (
    <>
      {contacts != null && contacts.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-xl text-gray-500 mb-4">
            Oops! It looks like you don't have any contacts yet.
          </p>
          <p className="text-gray-500 mb-6">
            Start adding some to stay connected!
          </p>
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md text-sm"
            onClick={() => navigate("/contacts")}
          >
            add a new contact
          </button>
        </div>
      ) : (
        <div className="bg-white min-h-screen">
          <div className="grid grid-cols-[1fr,auto] items-center">
            <div className="w-2/5 relative rounded-md shadow-sm">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <IoSearchOutline className="text-gray-800 sm:text-sm" />
              </div>
              <input
                type="text"
                className="block w-full rounded-md border-0 py-1.5 pl-7 pr-20 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                onChange={(e) => filterGroups(e.target.value)}
              />
            </div>
            <div className="flex flex-row space-x-4">
              <button
                type="button"
                className="border p-2 rounded-md bg-sky-400 hover:bg-sky-600 text-white text-sm"
                id="addGroup"
                disabled={contacts?.length === 0}
                onClick={() => toggleShowModal("create")}
              >
                <FaPlus />
              </button>
              <button
                type="button"
                className={`border p-2 rounded-md ${
                  selected?.ownerId === userId ? "text-red-700" : "opacity-25"
                }`}
                onClick={() => {
                  setIsDeleting(true);
                }}
                disabled={item?.ownerId !== userId}
              >
                <FaTrashAlt />
              </button>
            </div>
          </div>
          <div className="flex flex-col space-y-4">
            <div className="border rounded-lg">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="border-b border-r font-medium p-4 py-3 text-slate-400 dark:text-slate-200 text-left">
                      <div className="flex flex-row space-x-2 items-center">
                        <span>Group Name</span>
                        <FaSort />
                      </div>
                    </th>
                    <th className="border-b border-r font-medium p-4 pr-8 py-3 text-slate-400 dark:text-slate-200 text-left">
                      <div className="flex flex-row space-x-2 items-center">
                        <span>Owner</span>
                      </div>
                    </th>
                    <th className="border-b border-r font-medium p-4 pr-8 py-3 text-slate-400 dark:text-slate-200 text-left">
                      <div className="flex flex-row space-x-2 items-center">
                        <span>Members</span>
                      </div>
                    </th>
                    <th className="border-b font-medium p-4 py-3 text-slate-400 text-left"></th>
                  </tr>
                </thead>
                <tbody>
                  {!isGroupsLoading &&
                    filterResult.map((item) => (
                      <tr
                        data-id={item.id}
                        className={`cursor-pointer ${
                          item.id === selected?.id
                            ? "bg-slate-100"
                            : "hover:bg-slate-50"
                        }`}
                        key={item.id}
                        onClick={() => {
                          setSelected(item);
                          setItem((prev) => item);
                        }}
                      >
                        <td className="border-b border-r p-4 text-slate-500">
                          {item.name}
                        </td>
                        <td className="border-b border-r p-4 text-slate-500">
                          <div className="flex flex-row space-x-3 items-center">
                            <img
                              className="h-8 w-8 mr-2 object-contain border rounded-full hover:text-text-link"
                              src={
                                item.accounts?.find(
                                  (acc) => acc.accountId === item.ownerId
                                )?.pictureUrl || "/img/user.png"
                              }
                              alt="Group Owner"
                            />
                            {
                              item.accounts?.find(
                                (acc) => acc.accountId === item.ownerId
                              )?.username
                            }
                          </div>
                        </td>
                        <td className="border-b border-r p-4 text-slate-500">
                          {item.accounts
                            ?.filter((acc) => acc.accountId !== item.ownerId)
                            ?.map((ac) => ac.username)
                            ?.join(", ")}
                        </td>
                        {item.ownerId === userId && (
                          <td
                            className="border-r last-of-type:border-r-0 p-4 text-slate-500 hover:text-logo"
                            onClick={() => toggleShowModal("update")}
                          >
                            <MdEdit
                              size={20}
                              title="Edit"
                              className="mx-auto"
                            />
                          </td>
                        )}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {showModal && (
        <Modal
          show={modal?.show}
          component={modal?.component}
          onClose={closeModal}
        />
      )}
      {isDeleting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
            <h2 className="text-xl font-semibold mb-4">Delete Group?</h2>
            <p className="text-gray-600 mb-6">
              {`Are you sure you want to delete ${item?.name}?`}
            </p>
            <div className="flex justify-end">
              <button
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-md mr-2 text-sm"
                onClick={() => {
                  setIsDeleting(false);
                }}
              >
                Cancel
              </button>
              <button
                className={`bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-md text-sm ${
                  !item?.id ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={!item?.id}
                onClick={deleteGroup}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
            <div className="flex justify-center items-center">
              <Circles height="30" width="30" color="#2DE1FC" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactsGroups;
